import * as React from 'react';
import UserAddresses from 'common/components/UserAddresses/UserAddresses';
import style from './SingleUsersAddressDropdown.module.scss';
import { useTranslation } from 'react-i18next';
import { Button, Card } from 'antd';
import { PlusOutlined, CloseCircleFilled } from '@ant-design/icons';
import { PartyDto } from 'model/Api/Parties/Model/PartyDto';
import Flex from 'common/components/Flex';
import Link from 'web/components/Link';
import EditIcon from 'common/icons/edit_v2.svg';
import Add from 'common/icons/plus.svg';
import { observer } from 'mobx-react';

interface SingleUsersAddressDropdownProps {
    model: UserAddresses | undefined;
    type: 'private' | 'company';
    disabled?: boolean;
    witheClear?: boolean;
    userId?: number;
}

const SingleUsersAddressDropdown: React.FC<SingleUsersAddressDropdownProps> = observer(
    ({ model, type, disabled, witheClear = false, userId }) => {
        const { t } = useTranslation(['userAddresses', 'common']);

        if (!model || !model.isSelectable) {
            return null;
        }

        const selectedId = model.isDoubleSelection
            ? model.doubleSelectedParty[type]
            : model.selectedParty;

        const data: PartyDto[] = model.sortedParties[type] || [];

        if (!data.length) {
            return (
                <Button
                    disabled={disabled}
                    className={style.addButton}
                    type='text'
                    size='small'
                    onClick={() => model.onCreateAddress(type, userId)}>
                    <img src={Add} alt='add' />
                    {t('userAddresses:addAddress')}
                </Button>
            );
        }

        return (
            <div className={style.cardsContainer}>
                {data.map((party) => {
                    const isSelected = selectedId === party.id;

                    return (
                        <Card
                            key={party.id}
                            hoverable={!disabled}
                            onClick={() =>
                                !disabled &&
                                model.onSelectAddressForType(type, party.id)
                            }
                            className={`${style.card} ${
                                isSelected ? style.selected : ''
                            }`}>
                            <Flex
                                justifyContent='space-between'
                                alignItems='center'
                                flexDirection='column'
                                customClass={style.cardContent}>
                                <Flex
                                    justifyContent='space-between'
                                    alignItems='center'
                                    customClass={style.topRow}>
                                    <span className={style.dropdownText}>
                                        {!!party.name && `${party.name}, `}
                                    </span>
                                    <div className={style.actions}>
                                        <>
                                            <img
                                                src={EditIcon}
                                                alt='edit'
                                                className={style.dropdownIcon}
                                            />

                                            {witheClear && (
                                                <CloseCircleFilled
                                                    className={style.clear}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        model.onClear();
                                                    }}
                                                />
                                            )}
                                            <Link
                                                onClick={() => {
                                                    model.onEdit(party);
                                                }}
                                                style={style.dropdownLink}>
                                                {t('common:edit')}
                                            </Link>
                                        </>
                                    </div>
                                </Flex>
                                <ul className={style.addressText}>
                                    {!!party.careOf && (
                                        <li>
                                            {`${
                                                party.orgNo ? 'att. ' : 'c/o '
                                            } ${party.careOf}`}
                                        </li>
                                    )}

                                    {!!party.address.line && (
                                        <li>{party.address.line}</li>
                                    )}

                                    <li>
                                        {`${party.address.postalCode} ${party.address.city}`}
                                    </li>
                                </ul>
                            </Flex>
                        </Card>
                    );
                })}

                <Button
                    disabled={disabled}
                    className={style.addButton}
                    type='text'
                    size='small'
                    onClick={() => model.onCreateAddress(type, userId)}>
                    <img src={Add} alt='add' />
                    {t('userAddresses:addAddress')}
                </Button>
            </div>
        );
    }
);

export default SingleUsersAddressDropdown;
